<template>
  <div class="start-journey">
    <div class="start-journey__container">
      <div class="start-journey__container__content">
        <SimpleLandingLogo />

        <div
          :aria-label="titlePlainText"
          class="start-journey__container__image mobile"
          role="img"
          :style="{
            '--hero-image': `url(${mobileBannerImageV2})`,
            '--hero-image-tablet': `url(${mobileBannerImage})`,
          }"
        >
          <LandingIncentiveBadge :incentive="incentive" />
        </div>

        <h1
          class="start-journey__container__title kds-typography-display-small"
          v-html="title"
        />

        <p
          class="start-journey__container__description kds-typography-paragraph-large"
          v-html="description"
        />

        <BaseButton
          ref="ctaButton"
          class="start-journey__container__cta"
          data-qa="submit"
          @click="handleCTAButtonClick"
        >
          {{ ctaBtnTitle }}
        </BaseButton>

        <p
          v-if="!isWomenVariant"
          class="start-journey__container__members-proof"
          v-html="membersProofLine"
        />
      </div>

      <div
        :aria-label="titlePlainText"
        class="start-journey__container__image desktop"
        role="img"
        :style="{ '--hero-image': `url(${desktopBannerImage})` }"
      >
        <LandingIncentiveBadge :incentive="incentive" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import BaseButton from '@shared/components/BaseButton.vue'
import SimpleLandingLogo from '@us/app/components/SimpleLandingLogo.vue'
import mobileBannerImage from '@shared/assets/img/start-journey-banner-mobile.jpg'
import mobileBannerImageV2 from '@shared/assets/img/start-journey-banner-mobile-v2.jpg'
import desktopBannerImage from '@shared/assets/img/start-journey-banner-desktop.jpg'
import LandingIncentiveBadge from '@us/app/components/LandingIncentiveBadge.vue'
import { useI18n } from 'petite-vue-i18n'

const props = defineProps({
  ctaBtnTitle: {
    type: String,
    default: '',
  },
  handleCTAButtonClick: {
    type: Function,
    default: () => {},
  },
  receiveCTAButtonClientRect: {
    type: Function,
    default: () => {},
  },
  variant: {
    type: String,
    default: '',
  },
  incentive: {
    type: Object,
    default: null,
  },
})

const { t } = useI18n()

const ctaButton = ref()
const isWomenVariant = computed(() => props.variant === 'women')

const title = computed(() => {
  switch (props.variant) {
    case 'women':
      return t('b2b__landing_page__start_journey_us_women__title')
    default:
      return t('b2b__landing_page__start_journey_us__title')
  }
})

const stripHtml = (text) =>
  text
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
const titlePlainText = computed(() => stripHtml(title.value))

const description = computed(() => {
  switch (props.variant) {
    case 'women':
      return t('b2b__landing_page__start_journey_us_women__description')
    default:
      return t('b2b__landing_page__start_journey_us__description')
  }
})

const membersProofLine = computed(() =>
  t('b2b__landing_page__start_journey_us__members_line'),
)

onMounted(() => {
  props.receiveCTAButtonClientRect(() =>
    ctaButton?.value?.button?.getBoundingClientRect(),
  )
})
</script>

<style lang="scss" scoped>
.start-journey {
  padding: $kds-space-3x 10px 40px;
  background-color: $kds-color-background-base;

  @include media-up(lg) {
    --desktop-hero-right-bleed: max(
      0px,
      calc((100dvw - 1200px - #{$kds-space-1_5x}) / 2)
    );
    padding: 0px 0px 0px $kds-space-1_5x;
  }

  &__container {
    @include flex-container(column, flex-start, md);
    max-width: 1200px;
    margin: auto;

    @include media-up(lg) {
      @include flex-container(row, space-between, xl, stretch);
    }

    &__content {
      @include flex-container(column, flex-start, md);
      width: 100%;

      @include media-up(lg) {
        max-width: 560px;
        padding-top: $kds-space-4x;
        padding-bottom: 56px;
      }
    }

    &__title {
      margin: $kds-space-2_5x 0 $kds-space-1_5x;
      font-weight: $kds-typography-font-weight-medium;

      @include media-up(md) {
        @include kds-typography-display-medium;
        font-weight: $kds-typography-font-weight-medium;
      }
    }

    &__description {
      margin: 0 0 $kds-space-2_5x;

      :deep(.start-journey__description-break-desktop) {
        display: none;

        @include media-up(lg) {
          display: block;
        }
      }
    }

    &__cta {
      &.base-button {
        width: auto;
        align-self: center;
      }

      @include media-up(md) {
        &.base-button {
          width: fit-content;
          align-self: flex-start;
        }
      }
    }

    &__members-proof {
      margin: 14px 0 0;
      @include kds-typography-paragraph-large;
      color: $kds-color-text-secondary;

      :deep(strong) {
        color: $kds-color-text-primary;
        font-weight: $kds-typography-font-weight-bold;
      }

      @include media-up(md) {
        margin-top: $kds-space-2x;
        white-space: nowrap;
      }
    }

    &__image {
      display: block;
      position: relative;
      overflow: hidden;
      width: 100%;
      height: 264px;
      background: var(--hero-image) lightgray center/cover no-repeat;

      @include media-up(lg) {
        max-width: 560px;
        min-height: 380px;
        height: auto;
        align-self: stretch;
      }

      &.mobile {
        margin-top: $kds-space-1_5x;
        width: calc(100% + 20px);
        margin-left: -10px;
        margin-right: -10px;

        @include media-up(lg) {
          display: none;
        }
      }

      &.desktop {
        display: none;

        background-position: -280px -100px;

        //background: var(--hero-image) lightgray -245px -140px / 230% auto no-repeat;
        background: var(--hero-image) lightgray center/cover no-repeat;

        mask-image: radial-gradient(
          circle 850px at 850px 50%,
          /* Radius is 850px, Center is 850px from the LEFT edge */ black 0%,
          black 99.8%,
          /* The 0.2% gap creates a smoother, anti-aliased edge */ transparent
            100%
        );
        mask-repeat: no-repeat;
        mask-size: cover;

        @include media-up(lg) {
          display: block;
        }

        @include media-up(lg) {
          width: calc(560px + var(--desktop-hero-right-bleed));
          max-width: none;
          margin-right: calc(-1 * var(--desktop-hero-right-bleed));
        }
      }
    }
  }

  @include media-between(md, tablet-max) {
    &__container {
      &__image {
        &.mobile {
          background-image: var(--hero-image-tablet);
          height: auto;
          aspect-ratio: 3 / 2;
          background-size: contain;
          background-position: center;
          background-color: $kds-color-background-base;
        }
      }
    }
  }

  @include media-between(md, 1023px) {
    overflow-x: hidden;
    padding-bottom: 40px;

    &__container {
      flex-direction: column;
      gap: $kds-space-1_5x;

      &__content {
        max-width: none;
        padding-top: 0;
        padding-bottom: 0;
      }

      &__image {
        max-width: none;
        min-height: 0;
        height: auto;
        aspect-ratio: 3 / 2;
        align-self: auto;

        &.mobile {
          width: 100vw;
          margin-left: -10px;
          margin-right: 0;
          display: block;
        }

        &.desktop {
          display: none;
        }
      }
    }
  }

  @media (max-width: 1023px) and (max-height: 500px) and (orientation: landscape) {
    &__container {
      gap: $kds-space-1x;

      &__content {
        align-items: center;
        text-align: center;
      }

      &__cta {
        &.base-button {
          align-self: center;
        }
      }

      &__image {
        &.mobile {
          height: 290px;
          aspect-ratio: auto;
          width: calc(100% + 20px);
          margin-left: -10px;
          margin-right: -10px;
          background-image: var(--hero-image);
          background-position: center;
        }
      }
    }

    :deep(.simple-landing-logo) {
      justify-content: center;
    }

    :deep(.simple-landing-logo__kaia) {
      background-image: url('@shared/assets/img/kaia_text_logo.svg');
      height: 15px;
      width: 100px;
    }

    :deep(.simple-landing-logo__corporate) {
      max-height: 44px;
      max-width: 200px;
    }
  }
}
</style>
